import { createContext, FC, useCallback, useContext, useMemo } from 'react';
import { DELETE_ALL_OPEN_SHIFTS } from 'constants/endpoints';
import { METHOD_DELETE } from 'requests';
import { useAPIActions } from 'store/reducers/api/apiSlice';
import { ApiRequestConfigType } from 'requests/index.types';
import { handleGenericErrorMessage } from 'utils/handleGenericErrorMessage';
import { DeleteAllOpenShiftsDialogProps } from 'pages/LocationSettingsPage/PositionsAllocation/DeleteAllOpenShiftsDialog/DeleteAllOpenShiftsDialog.types';
import { toastService } from 'services/toastService';
import { useTranslation } from 'react-i18next';
import { useOpenShiftsDataContext } from 'pages/LocationSettingsPage/PositionsAllocation/RemovePositionModal/views/OpenShifts/OpenShifts.data.context';

interface DeleteOpenShiftsDialogForm {
  handleAllOpenShifts: () => void;
  communityName?: string;
}

const API_DELETE_OpenShifts = (
  community_id: string,
  position_id: number,
): ApiRequestConfigType => ({
  __typename: 'LegacyAPI',
  url: DELETE_ALL_OPEN_SHIFTS(community_id, position_id),
  method: METHOD_DELETE,
});

export const DeleteAllOpenShiftsDialogFormContext = createContext({} as DeleteOpenShiftsDialogForm);

export const DeleteAllOpenShiftsDialogFormProvider: FC<DeleteAllOpenShiftsDialogProps> = ({
  children,
  communityId,
  positionId,
  communityName,
}) => {
  const { apiRequest } = useAPIActions();
  const { t } = useTranslation();
  const { refetch } = useOpenShiftsDataContext();

  const handleAllOpenShifts = useCallback(
    async () =>
      await apiRequest(API_DELETE_OpenShifts(communityId, positionId), {
        onSuccess: () => {
          toastService.success(t('success:SHIFTS_SUCCESSFULLY_DELETED'));
          refetch && refetch();
        },
        onError: handleGenericErrorMessage,
      }),
    [apiRequest, communityId, positionId, t, refetch],
  );

  const contextValue = useMemo(
    () => ({
      handleAllOpenShifts,
      communityName,
    }),
    [handleAllOpenShifts, communityName],
  );

  return (
    <DeleteAllOpenShiftsDialogFormContext.Provider value={contextValue}>
      {children}
    </DeleteAllOpenShiftsDialogFormContext.Provider>
  );
};

export const useDeleteAllOpenShiftsDialogFormProviderFormContext = () =>
  useContext(DeleteAllOpenShiftsDialogFormContext);
